import React, { useState, useEffect } from 'react';
import { Card, Stack, Button, Drawer } from '@mui/material';
import { ACTIONS } from '../../config/const.config';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/users.config';
import ListData from '../table-elements/list-data.component';
import { getListUser } from '../../services/users.service';
import ViewUser from './view-user.component';
import Iconify from '../common/iconify.component';
import FilterUsers from './filter-users.component';
import { formatDateToUtc } from '../../utils/datetime.util';

const ListUsers = () => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 20,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    q: null,
    error: false,
    loading: true,
    appliedFilters: {
      search: '',
      filterApp: null,
      platforms: [],
      brd: null,
      brdsdkversion: null,
      startDate: null,
      endDate: null,
      sessionStartDate: null,
      sessionEndDate: null,
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [openFilters, setOpenFilters] = useState(false);

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleToggleFilters = () => {
    setOpenFilters(!openFilters);
  };
  const handleApplyFilters = (selectedFilters) => {
    let counter = 0;
    if (selectedFilters.search.trim()) counter += 1;
    if (selectedFilters.filterApp) counter += 1;
    if (selectedFilters.platforms.length > 0) counter += 1;
    if (selectedFilters.brd !== null && selectedFilters.brd !== '')
      counter += 1;
    if (
      selectedFilters.brdsdkversion !== null &&
      selectedFilters.brdsdkversion !== ''
    )
      counter += 1;
    if (selectedFilters.startDate) counter += 1;
    if (selectedFilters.endDate) counter += 1;
    if (selectedFilters.sessionStartDate) counter += 1;
    if (selectedFilters.sessionEndDate) counter += 1;

    setOptions({
      ...options,
      appliedFilters: {
        ...selectedFilters,
      },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
    handleToggleFilters();
  };
  const handleClearFilters = () => {
    handleToggleFilters();
    setOptions({
      ...options,
      appliedFilters: {
        search: '',
        filterApp: null,
        platforms: [],
        brd: null,
        brdsdkversion: null,
        startDate: null,
        endDate: null,
        sessionStartDate: null,
        sessionEndDate: null,
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = options.appliedFilters;
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }
    if (filters.filterApp && filters.filterApp.id) {
      params.push(`appId=${encodeURIComponent(filters.filterApp.id)}`);
    }
    if (filters.platforms.length > 0) {
      const platforms = [];
      filters.platforms.forEach((p) => platforms.push(p.id));
      params.push(`platforms=${platforms.join(',')}`);
    }
    if (filters.brd !== null && filters.brd !== '') {
      params.push(`brd=${filters.brd.id}`);
    }
    if (filters.brdsdkversion !== null && filters.brdsdkversion !== '') {
      params.push(`brdsdkversion=${filters.brdsdkversion.id}`);
    }
    if (filters.startDate) {
      const startDate = formatDateToUtc(filters.startDate);
      params.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (filters.endDate) {
      const endDate = formatDateToUtc(filters.endDate);
      params.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    if (filters.sessionStartDate) {
      const sessionStartDate = formatDateToUtc(filters.sessionStartDate);
      params.push(`sessionStartDate=${encodeURIComponent(sessionStartDate)}`);
    }
    if (filters.sessionEndDate) {
      const sessionEndDate = formatDateToUtc(filters.sessionEndDate);
      params.push(`sessionEndDate=${encodeURIComponent(sessionEndDate)}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListUser(paramsQuery)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          error: false,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <div>&nbsp;</div>
        <div>
          <Button
            color={options.filtersCounter > 0 ? 'warning' : 'inherit'}
            variant="contained"
            startIcon={<Iconify icon="ic:round-filter-list" />}
            sx={{ marginRight: 2 }}
            onClick={handleToggleFilters}
          >
            Filters
            {options.filtersCounter > 0 ? ` (${options.filtersCounter})` : ''}
          </Button>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
        />
      </Card>

      <Drawer
        anchor="right"
        open={openFilters}
        onClose={handleToggleFilters}
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'hidden' },
        }}
      >
        <FilterUsers
          values={options.appliedFilters}
          onCancel={handleToggleFilters}
          onSuccess={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </Drawer>

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewUser
          title={`User Details - ${doAction.data.deviceId}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}
    </>
  );
};

export default ListUsers;
